import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import {redirectForDownload, setWindowTitle} from 'BenchlingUtil';
import ChemicalStructureSVG from 'chemistry/ChemicalStructureSVG';
import {
  MonomerNaturalAnalogue,
  MonomerPolymerType,
  MonomerVisualSymbol,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import navigateToURL from 'lib/navigateToURL';
import {
  archiveMonomers,
  exportCustomSyntaxesAsCSV,
  exportMonomersAsCSV,
  unarchiveMonomers,
  updateMonomer,
} from 'molbio/monomers/MonomersDAO';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {Header1} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_KEYS,
} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';
import * as net from 'util/Net';

import {MonomerQuery} from './__generated/MonomerQuery';
import {isMonomerManagementEnabledForUser} from './admin/util';
import MonomerColorDropdown, {MonomerColorIconType} from './MonomerColorDropdown';
import {
  DEFAULT_MODIFIED_BASE_COLOR,
  DEFAULT_MODIFIED_SUGAR_COLOR,
  MONOMER_ANALOGUE_DISPLAY_NAME,
  MONOMER_QUERY,
  MONOMER_TYPE_DISPLAY_NAME,
  NaturalAnalogueToNucleotideComponent,
  NucleotideComponent,
  RECOMMENDED_CUSTOM_BASE_COLORS,
  RECOMMENDED_CUSTOM_SUGAR_COLORS,
} from './MonomerConstants';
import MonomerListingFilters from './MonomerListingFilters';
import MonomerVisualSymbolDropdown from './MonomerVisualSymbolDropdown';

type Monomer = MonomerQuery['monomers'][number];

const TABLE_ROW_HEIGHT = 80;

interface MonomerRowSortableData {
  name: string;
  symbol: string;
  naturalAnalogue: string;
  polymerType: MonomerPolymerType;
  monomerType: string;
  canonicalSmiles: string;
  originalSmiles: string;
  calculatedMolecularWeight: number;
  calculatedExactMolecularWeight: number;
}

type MonomerRowKeys = keyof MonomerRowSortableData;

interface MonomerRowData extends MonomerRowSortableData {
  visualElement: JSX.Element;
  chemicalStructureImage: JSX.Element;
  actions: JSX.Element;
  isNatural: boolean;
  customMolecularWeight: number | null;
  id: string;
}

export interface MonomerRow extends ScrollableTableRow {
  data: MonomerRowData;
}

interface MonomerColumn extends ScrollableTableColumn {
  sortKey?: MonomerRowKeys;
}

const MONOMER_COLUMNS: Array<MonomerColumn> = [
  {
    header: 'Name',
    accessorKey: 'name',
    flexGrow: 1,
    isCollapsible: false,
    isSortable: true,
    sortKey: 'name',
  },
  {
    header: 'Symbol',
    accessorKey: 'symbol',
    isCollapsible: true,
    isSortable: true,
    sortKey: 'symbol',
  },
  {
    header: 'Chemical structure',
    accessorKey: 'chemicalStructureImage',
    flexGrow: 1,
    isCollapsible: true,
    isSortable: false,
  },
  {
    header: 'Visual',
    accessorKey: 'visualElement',
    isCollapsible: true,
    isSortable: false,
  },
  {
    header: 'Natural Analogue',
    accessorKey: 'naturalAnalogue',
    isCollapsible: true,
    isSortable: true,
    sortKey: 'naturalAnalogue',
  },
  {
    header: 'Polymer Type',
    accessorKey: 'polymerType',
    isCollapsible: true,
    isSortable: true,
    sortKey: 'polymerType',
  },
  {
    header: 'Monomer Type',
    accessorKey: 'monomerType',
    isCollapsible: true,
    isSortable: true,
    sortKey: 'monomerType',
  },
  {
    header: 'Canonical SMILES',
    accessorKey: 'canonicalSmiles',
    flexGrow: 1,
    isCollapsible: true,
    isSortable: false,
    sortKey: 'canonicalSmiles',
    wrapText: true,
  },
  {
    header: 'Original SMILES',
    accessorKey: 'originalSmiles',
    flexGrow: 1,
    isCollapsible: true,
    isSortable: false,
    sortKey: 'originalSmiles',
    wrapText: true,
  },
  {
    header: 'Calculated molecular weight',
    accessorKey: 'calculatedMolecularWeight',
    flexGrow: 1,
    isCollapsible: true,
    isSortable: true,
    sortKey: 'calculatedMolecularWeight',
    wrapText: true,
  },
  {
    header: 'Calculated exact molecular weight',
    accessorKey: 'calculatedExactMolecularWeight',
    flexGrow: 1,
    isCollapsible: true,
    isSortable: true,
    sortKey: 'calculatedExactMolecularWeight',
    wrapText: true,
  },
  {
    header: 'Custom molecular weight',
    accessorKey: 'customMolecularWeight',
    flexGrow: 1,
    isCollapsible: true,
    isSortable: false,
    wrapText: true,
  },
  {
    header: 'ID',
    accessorKey: 'id',
    flexGrow: 1,
    isCollapsible: true,
    isSortable: true,
    wrapText: true,
  },
  {
    header: 'Actions',
    accessorKey: 'actions',
    isCollapsible: false,
    isSortable: false,
  },
];

function getMonomerVisual(monomer: Monomer): MonomerVisualSymbol | string {
  const nucleotideComponent = NaturalAnalogueToNucleotideComponent[monomer.naturalAnalogue];
  if (nucleotideComponent === NucleotideComponent.PHOSPHATE) {
    return monomer.visualSymbol ?? MonomerVisualSymbol.DIAMOND_FILLED;
  } else if (nucleotideComponent === NucleotideComponent.SUGAR) {
    return monomer.color ?? DEFAULT_MODIFIED_SUGAR_COLOR;
  } else {
    return monomer.color ?? DEFAULT_MODIFIED_BASE_COLOR;
  }
}

export default function MonomerListing(): JSX.Element {
  useEffect(() => {
    setWindowTitle('Monomers');
  }, []);
  const [selectedNaturalAnalogues, selectNaturalAnalogues] = useState<Array<MonomerNaturalAnalogue>>([]);
  const [selectedArchivePurposes, selectArchivePurposes] = useState<Array<string>>([
    NOT_ARCHIVED_PURPOSE_STRING,
  ]);

  const [query, setQuery] = useState<string>('');
  const canEdit = EntityStore.currentUser().isTenantAdmin;

  const downloadCsv = async (exportFn: () => Promise<{s3Location: string}>): Promise<void> => {
    try {
      notifications.info('Downloading...');
      const {s3Location} = await exportFn();
      redirectForDownload(s3Location);
    } catch (e) {
      if (net.isServerRequestError(e)) {
        notifications.notifyError(e);
      }
      throw e;
    }
  };
  return (
    <div
      className={css(styles.monomerPage)}
      {...getTestProps({component: 'MonomerListing', element: 'root'})}
    >
      <div className={css(styles.monomerListing)}>
        <Header1 className={css(styles.headerSection)}>Monomer library</Header1>
        <BenchlingQuery<MonomerQuery>
          query={MONOMER_QUERY}
          renderLoading={() => <CenteredHorizontalThrobber />}
          fetchPolicy="cache-and-network"
          renderCachedDataWhenRefetching={true}
        >
          {({monomers}, {refetch}) => {
            // eslint-disable-next-line benchlint/no-spread-reduce
            const monomerVisuals: {[id: string]: MonomerVisualSymbol | string} = monomers.reduce(
              (visuals, monomer) => ({...visuals, [monomer.id]: getMonomerVisual(monomer)}),
              {}
            );
            const usedColors = Object.values(monomerVisuals).filter(
              (visual) =>
                ![
                  DEFAULT_MODIFIED_BASE_COLOR,
                  DEFAULT_MODIFIED_SUGAR_COLOR,
                  ...Object.values(MonomerVisualSymbol),
                ].includes(visual.toUpperCase())
            );
            const rows: Array<MonomerRow> = monomers
              .filter(
                (monomer) =>
                  selectedNaturalAnalogues.length === 0 ||
                  selectedNaturalAnalogues.includes(monomer.naturalAnalogue)
              )
              .filter(
                (monomer) =>
                  (selectedArchivePurposes.includes(NOT_ARCHIVED_PURPOSE_STRING) && !monomer.archiveRecord) ||
                  (monomer.archiveRecord && selectedArchivePurposes.includes(monomer.archiveRecord.purpose))
              )
              .filter(
                (monomer) =>
                  query === '' ||
                  monomer.name.toLowerCase().includes(query.toLowerCase()) ||
                  monomer.symbol.toLowerCase().includes(query.toLowerCase())
              )
              .map((monomer) => {
                const nucleotideComponent = NaturalAnalogueToNucleotideComponent[monomer.naturalAnalogue];
                const disableVisualElementEdits = !canEdit || Boolean(monomer.archiveRecord);
                const actions = (
                  <div className={css(styles.actionButtons)}>
                    {isMonomerManagementEnabledForUser() && !monomer.isNatural && (
                      <ArchiveOrUnarchiveButton
                        item={monomer}
                        onArchive={async (itemIds, purpose) => {
                          LoadingScreen.show();
                          await archiveMonomers(itemIds, purpose);
                          await refetch();
                          LoadingScreen.hide();
                        }}
                        onUnarchive={async (itemIds) => {
                          LoadingScreen.show();
                          await unarchiveMonomers(itemIds);
                          await refetch();
                          LoadingScreen.hide();
                        }}
                        isArchivable={canEdit}
                        isUnarchivable={canEdit}
                      />
                    )}
                    {isMonomerManagementEnabledForUser() && !monomer.isNatural && !monomer.archiveRecord && (
                      <Tooltip title="Edit">
                        <Button
                          icon="edit"
                          onClick={() => {
                            navigateToURL(`/monomers/${monomer.id}/edit`);
                          }}
                          size={ButtonSize.SMALL}
                          buttonStyle={ButtonStyle.TERTIARY_GRAY}
                          dataTestAttrs={{component: 'MonomerListing', element: 'editButton'}}
                        />
                      </Tooltip>
                    )}
                    <Tooltip title={EXPORT_AUDIT_LOG_DISPLAY_STRING}>
                      <Button
                        icon="audit-trail"
                        onClick={() => {
                          exportAuditLogs([monomer.id]);
                        }}
                        size={ButtonSize.SMALL}
                        buttonStyle={ButtonStyle.TERTIARY_GRAY}
                      />
                    </Tooltip>
                  </div>
                );

                return {
                  id: monomer.id,
                  data: {
                    name: monomer.name,
                    symbol: monomer.symbol,
                    naturalAnalogue: MONOMER_ANALOGUE_DISPLAY_NAME[monomer.naturalAnalogue],
                    polymerType: monomer.polymerType,
                    monomerType: MONOMER_TYPE_DISPLAY_NAME[monomer.monomerType],
                    canonicalSmiles: monomer.canonicalSmiles,
                    originalSmiles: monomer.originalSmiles,
                    isNatural: monomer.isNatural,
                    calculatedMolecularWeight: monomer.calculatedMolecularWeight ?? 0,
                    customMolecularWeight: monomer.customMolecularWeight,
                    calculatedExactMolecularWeight: monomer.calculatedExactMolecularWeight ?? 0,
                    id: monomer.id,
                    chemicalStructureImage: (
                      <ChemicalStructureSVG
                        chemicalStructureImage={monomer.chemicalStructureImage}
                        className={css(styles.chemicalStructureContainer)}
                        svgClassName={css(styles.chemicalStructureSvg)}
                      />
                    ),
                    visualElement:
                      nucleotideComponent === NucleotideComponent.PHOSPHATE ? (
                        <MonomerVisualSymbolDropdown
                          selectedSymbol={monomerVisuals[monomer.id] as MonomerVisualSymbol}
                          monomerId={monomer.id}
                          onSelectSymbol={async (visualSymbol: MonomerVisualSymbol) => {
                            await updateMonomer(monomer.id, {visualSymbol});
                            refetch();
                          }}
                          isDisabled={disableVisualElementEdits}
                        />
                      ) : (
                        <MonomerColorDropdown
                          selectedColor={monomerVisuals[monomer.id]}
                          recommendedColors={
                            nucleotideComponent === NucleotideComponent.BASE
                              ? RECOMMENDED_CUSTOM_BASE_COLORS
                              : RECOMMENDED_CUSTOM_SUGAR_COLORS
                          }
                          iconType={
                            nucleotideComponent === NucleotideComponent.BASE
                              ? MonomerColorIconType.MODIFIED_BASE
                              : MonomerColorIconType.MODIFIED_SUGAR
                          }
                          monomerId={monomer.id}
                          onSelectColor={async (color: string) => {
                            await updateMonomer(monomer.id, {color});
                            refetch();
                          }}
                          isDisabled={disableVisualElementEdits}
                          allUsedColors={usedColors}
                        />
                      ),
                    actions,
                  },
                };
              });
            return (
              <div>
                <div className={css(styles.headerSection)}>
                  <div className="u-lr-split u-spacing-right">
                    <MonomerListingFilters
                      handleArchivePurposeFilter={selectArchivePurposes}
                      handleNaturalAnalogueFilter={selectNaturalAnalogues}
                      handleQueryFilter={setQuery}
                      archivePurposes={selectedArchivePurposes}
                      naturalAnalogues={selectedNaturalAnalogues}
                      query={query}
                      monomerIds={monomers.map((m) => m.id)}
                    />
                    <div className={css(styles.topButtons)}>
                      <Dropdown
                        menu={
                          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
                            <Option
                              key={1}
                              onSelect={() => {
                                downloadCsv(() => exportMonomersAsCSV(false));
                              }}
                            >
                              Export monomers as CSV with all fields
                            </Option>
                            <Option
                              key={2}
                              onSelect={() => {
                                downloadCsv(() => exportMonomersAsCSV(true));
                              }}
                            >
                              Export monomers as CSV for reimport into Benchling
                            </Option>
                            {BenchlingConfig.MODBIO_ENABLE_CUSTOM_SYNTAXES && (
                              <Option
                                key={3}
                                onSelect={() => {
                                  downloadCsv(() => exportCustomSyntaxesAsCSV());
                                }}
                              >
                                Export custom notations as CSV
                              </Option>
                            )}
                          </Menu>
                        }
                      >
                        <Button
                          buttonStyle={ButtonStyle.TERTIARY_GRAY}
                          size={ButtonSize.SMALL}
                          icon="export"
                          isDropdown={true}
                          dataTestAttrs={{
                            component: 'MonomerListing',
                            element: 'exportButton',
                          }}
                        />
                      </Dropdown>
                      <Tooltip title="Export audit logs for all monomers">
                        <Button
                          icon="audit-trail"
                          onClick={() => {
                            exportAuditLogs(monomers.map((mon) => mon.id));
                          }}
                          size={ButtonSize.SMALL}
                          buttonStyle={ButtonStyle.TERTIARY_GRAY}
                          dataTestAttrs={{
                            component: 'MonomerListing',
                            element: 'auditAllButton',
                          }}
                        />
                      </Tooltip>
                      {isMonomerManagementEnabledForUser() && BenchlingConfig.MOLBIO_ENABLE_IDT_SYNTAX && (
                        <Button
                          buttonStyle={ButtonStyle.TERTIARY_GRAY}
                          size={ButtonSize.SMALL}
                          text="IDT aliases"
                          onClick={() => {
                            navigateToURL('/monomers/idt-aliases');
                          }}
                        />
                      )}
                      {isMonomerManagementEnabledForUser() &&
                        BenchlingConfig.MODBIO_ENABLE_CUSTOM_SYNTAXES && (
                          <Button
                            buttonStyle={ButtonStyle.TERTIARY_GRAY}
                            size={ButtonSize.SMALL}
                            text="Custom notations"
                            onClick={() => {
                              navigateToURL('/monomers/custom-notations');
                            }}
                          />
                        )}
                      {isMonomerManagementEnabledForUser() ? (
                        <Button
                          buttonStyle={ButtonStyle.SECONDARY}
                          size={ButtonSize.SMALL}
                          text="Add new monomer"
                          onClick={() => {
                            navigateToURL('/monomers/create');
                          }}
                          dataTestAttrs={{
                            component: 'MonomerListing',
                            element: 'addNewMonomerButton',
                          }}
                        />
                      ) : (
                        <Button
                          buttonStyle={ButtonStyle.TERTIARY_GRAY}
                          size={ButtonSize.SMALL}
                          text="Browse HELM Library"
                          onClick={() => {
                            navigateToURL('/monomers/view/helm-library');
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <SortableScrollableTable
                  canResizeColumns={true}
                  columns={MONOMER_COLUMNS}
                  getRowContentHeight={() => TABLE_ROW_HEIGHT}
                  emptyTableText="No monomers were found."
                  getSortValue={(row: MonomerRow, sortKey: MonomerRowKeys) => row.data[sortKey]}
                  initialRows={rows}
                  initialSort={[{sortKey: 'name'}]}
                  tableKey={TABLE_KEYS.MONOMER_LISTING_TABLE}
                  calcLeftPadding={() => 40}
                />
              </div>
            );
          }}
        </BenchlingQuery>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  monomerListing: {
    margin: '40px 0',
  },
  monomerPage: {
    overflowY: 'auto',
    height: '100%',
  },
  headerSection: {
    marginLeft: '40px',
  },
  chemicalStructureContainer: {
    height: `${TABLE_ROW_HEIGHT}px`,
    textAlign: 'center',
  },
  chemicalStructureSvg: {
    width: '100%',
    height: '100%',
  },
  topButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 4,
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'space-evenly',
  },
});
